import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Play, Pause, RotateCcw, Settings as SettingsIcon, Save, Trash2,
  Gamepad2, MonitorSmartphone, MonitorOff, VolumeX,
} from "lucide-react";
import { useI18n } from "../context/I18nContext";

const BUILTIN = [
  { key: "arrows3", prep: 10, shoot: 90, orange: 30, red: 10 },
  { key: "arrows6", prep: 10, shoot: 180, orange: 30, red: 10 },
];

const LS_KEY = "bowlink_chrono_presets";
const LS_PAD = "bowlink_chrono_pad";
// Indices de la Gamepad API "standard" : 0 = bouton du bas, 1 = bouton de droite…
// Réassignable depuis l'écran de réglage (selon la manette / le mode, A et B ne sont pas toujours 0 et 1).
const DEFAULT_PAD = { playPause: 0, restart: 1 };

const fmt = (s) => `${Math.max(0, Math.ceil(s))}s`;

const loadPad = () => {
  try {
    const p = JSON.parse(localStorage.getItem(LS_PAD) || "null");
    if (p && Number.isInteger(p.playPause) && Number.isInteger(p.restart)) return p;
  } catch { /* */ }
  return DEFAULT_PAD;
};

const chipStyle = (color) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "11px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  padding: "6px 10px",
  borderRadius: "999px",
  background: "rgba(15,23,42,0.75)",
  color,
});

export default function Chrono() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // ─── Réglages (identiques à la fenêtre chrono) ───
  const [prep, setPrep] = useState("10");
  const [shoot, setShoot] = useState("120");
  const [orange, setOrange] = useState("30");
  const [red, setRed] = useState("10");
  const [name, setName] = useState("");
  const [showErrors, setShowErrors] = useState(false);
  const [nameTried, setNameTried] = useState(false);

  const [phase, setPhase] = useState("idle"); // idle | prep | shoot | done
  const [remaining, setRemaining] = useState(0);
  const [paused, setPaused] = useState(false);

  const [saved, setSaved] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "[]"); } catch { return []; }
  });

  // ─── État propre à la page ───
  const [wake, setWake] = useState("pending"); // pending | on | off | unsupported
  const [audioReady, setAudioReady] = useState(false);
  const [pad, setPad] = useState(loadPad);
  const [assigning, setAssigning] = useState(null); // null | "playPause" | "restart"
  const [padName, setPadName] = useState("");

  const endRef = useRef(0);
  const pauseRef = useRef(0);
  const cfgRef = useRef({ prep: 10, shoot: 120, orange: 30, red: 10 });
  const acRef = useRef(null);
  const bufRef = useRef(null);
  const bufPromiseRef = useRef(null);
  const wakeRef = useRef(null);
  const mountedRef = useRef(false);
  const actionsRef = useRef({ playPause: () => {}, restart: () => {} });
  const padHandlerRef = useRef(() => {});

  // ─── Validation (champs vides autorisés, on bloque juste le lancement) ───
  const nPrep = prep === "" ? null : parseInt(prep, 10);
  const nShoot = shoot === "" ? null : parseInt(shoot, 10);
  const nOrange = orange === "" ? null : parseInt(orange, 10);
  const nRed = red === "" ? null : parseInt(red, 10);
  const errPrep = nPrep === null;
  const errShoot = nShoot === null || nShoot < 1;
  const errOrange = nOrange === null || (nShoot !== null && nOrange > nShoot);
  const errRed = nRed === null || (nOrange !== null && nRed > nOrange);
  const invalid = errPrep || errShoot || errOrange || errRed;

  // ─── Audio ───
  // Le contexte est créé et "resume()" appelé de façon synchrone (dans le geste de l'utilisateur),
  // ce que Safari/iPadOS exige ; le chargement du son se fait ensuite.
  const ensureAudio = useCallback(async () => {
    try {
      if (!acRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        acRef.current = new AC();
      }
      const ac = acRef.current;
      if (ac.state === "suspended") ac.resume().catch(() => {});
      if (!bufPromiseRef.current) {
        bufPromiseRef.current = fetch("/sounds/beep.wav")
          .then((r) => r.arrayBuffer())
          .then((arr) => ac.decodeAudioData(arr))
          .then((buf) => { bufRef.current = buf; })
          .catch(() => { bufPromiseRef.current = null; });
      }
      await bufPromiseRef.current;
      if (mountedRef.current) setAudioReady(ac.state === "running");
    } catch { /* ignore */ }
  }, []);

  const closeAudio = useCallback(() => {
    try {
      const p = acRef.current && acRef.current.close();
      if (p && p.catch) p.catch(() => {});
    } catch { /* */ }
    acRef.current = null;
    bufRef.current = null;
    bufPromiseRef.current = null;
  }, []);

  const beep = useCallback((times = 1) => {
    const ac = acRef.current, buf = bufRef.current;
    if (ac && buf) {
      for (let i = 0; i < times; i++) {
        const src = ac.createBufferSource();
        src.buffer = buf; src.connect(ac.destination);
        src.start(ac.currentTime + i * (buf.duration + 0.12));
      }
    } else {
      for (let i = 0; i < times; i++) {
        try { const a = new Audio("/sounds/beep.wav"); setTimeout(() => a.play().catch(() => {}), i * 1150); } catch { /* */ }
      }
    }
  }, []);

  // ─── Anti-veille : actif tant que la page est ouverte (réglages compris) ───
  const acquireWake = useCallback(async () => {
    if (!("wakeLock" in navigator)) {
      if (mountedRef.current) setWake("unsupported");
      return;
    }
    try {
      if (wakeRef.current && !wakeRef.current.released) return;
      const lock = await navigator.wakeLock.request("screen");
      if (!mountedRef.current) { lock.release().catch(() => {}); return; }
      wakeRef.current = lock;
      setWake("on");
      lock.addEventListener("release", () => { if (mountedRef.current) setWake("off"); });
    } catch {
      if (mountedRef.current) setWake("off");
    }
  }, []);

  const releaseWake = useCallback(() => {
    try { wakeRef.current && wakeRef.current.release(); } catch { /* */ }
    wakeRef.current = null;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    acquireWake();
    ensureAudio();
    // Le verrou est relâché par le navigateur quand l'onglet passe en arrière-plan : on le reprend au retour.
    const onVis = () => { if (document.visibilityState === "visible") acquireWake(); };
    // Certains navigateurs exigent un geste pour accorder le verrou / débloquer le son : on retente au premier contact.
    const onGesture = () => { acquireWake(); ensureAudio(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      releaseWake();
      closeAudio();
    };
  }, [acquireWake, releaseWake, ensureAudio, closeAudio]);

  // ─── Boucle du chrono ───
  const startShoot = () => {
    setPhase("shoot");
    endRef.current = Date.now() + cfgRef.current.shoot * 1000;
    setRemaining(cfgRef.current.shoot);
    beep(2);
  };

  useEffect(() => {
    if (phase === "idle" || phase === "done" || paused) return;
    const iv = setInterval(() => {
      const rem = (endRef.current - Date.now()) / 1000;
      if (rem <= 0) {
        if (phase === "prep") { startShoot(); }
        else { setPhase("done"); setRemaining(0); beep(3); }
      } else {
        setRemaining(rem);
      }
    }, 100);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused]);

  const launch = () => {
    if (cfgRef.current.prep > 0) {
      setPhase("prep");
      endRef.current = Date.now() + cfgRef.current.prep * 1000;
      setRemaining(cfgRef.current.prep);
    } else {
      startShoot();
    }
  };

  const start = async () => {
    if (invalid) { setShowErrors(true); return; }
    try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch { /* */ }
    cfgRef.current = { prep: nPrep, shoot: nShoot, orange: nOrange, red: nRed };
    await ensureAudio();
    acquireWake();
    setPaused(false);
    beep(1);
    launch();
  };

  const pause = () => { pauseRef.current = (endRef.current - Date.now()) / 1000; setPaused(true); };
  const resume = () => { endRef.current = Date.now() + pauseRef.current * 1000; setPaused(false); };
  const reset = () => { setPhase("idle"); setPaused(false); setRemaining(0); };

  const restart = async () => {
    await ensureAudio();
    acquireWake();
    setPaused(false);
    beep(1);
    launch();
  };

  // A : démarrer / pause / reprendre (et relancer quand le temps est écoulé)
  const playPause = () => {
    if (phase === "idle") { start(); return; }
    if (phase === "done") { restart(); return; }
    if (paused) resume(); else pause();
  };
  // B : redémarrer (sans effet tant qu'aucun chrono n'a été lancé)
  const restartAction = () => {
    if (phase === "idle") return;
    restart();
  };

  // ─── Manette : assignation d'un bouton ───
  const persistPad = (next) => {
    setPad(next);
    try { localStorage.setItem(LS_PAD, JSON.stringify(next)); } catch { /* */ }
  };
  const assignButton = (idx) => {
    const key = assigning;
    if (!key) return;
    const other = key === "playPause" ? "restart" : "playPause";
    const next = { ...pad, [key]: idx };
    if (pad[other] === idx) next[other] = pad[key]; // on échange pour ne jamais avoir deux actions sur le même bouton
    persistPad(next);
    setAssigning(null);
  };

  // Les écouteurs ci-dessous sont posés une seule fois : ils passent par ces refs pour toujours voir l'état courant.
  useEffect(() => {
    actionsRef.current = { playPause, restart: restartAction };
    padHandlerRef.current = (idx) => {
      if (assigning) { assignButton(idx); return; }
      if (idx === pad.playPause) actionsRef.current.playPause();
      else if (idx === pad.restart) actionsRef.current.restart();
    };
  });

  // Touches clavier A et B (ignorées dans les champs de texte ; les champs numériques ne reçoivent jamais de lettres)
  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      const tag = el && el.tagName;
      const isTextField = tag === "TEXTAREA" || (tag === "INPUT" && el.inputMode !== "numeric") || (el && el.isContentEditable);
      if (isTextField) return;
      const k = (e.key || "").toLowerCase();
      if (k === "a") { e.preventDefault(); actionsRef.current.playPause(); }
      else if (k === "b") { e.preventDefault(); actionsRef.current.restart(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Manette (Gamepad API) : on lit l'état des boutons toutes les 50 ms et on réagit au front montant.
  useEffect(() => {
    const prev = {};
    const iv = setInterval(() => {
      let pads = [];
      try { pads = navigator.getGamepads ? Array.from(navigator.getGamepads()) : []; } catch { /* */ }
      let found = "";
      pads.forEach((gp) => {
        if (!gp) return;
        if (!found) found = (gp.id || "").split("(")[0].trim() || "Gamepad";
        gp.buttons.forEach((b, i) => {
          const k = `${gp.index}:${i}`;
          const down = b.pressed || b.value > 0.5;
          if (down && !prev[k]) padHandlerRef.current(i);
          prev[k] = down;
        });
      });
      setPadName(found);
    }, 50);
    return () => clearInterval(iv);
  }, []);

  // ─── Chronos enregistrés ───
  const persist = (list) => { setSaved(list); try { localStorage.setItem(LS_KEY, JSON.stringify(list)); } catch { /* */ } };
  const savePreset = () => {
    if (invalid) { setShowErrors(true); return; }
    if (!name.trim()) { setNameTried(true); return; }
    persist([...saved, { name: name.trim(), prep: nPrep, shoot: nShoot, orange: nOrange, red: nRed }]);
    setName("");
  };
  const applyPreset = (p) => {
    setPrep(String(p.prep)); setShoot(String(p.shoot)); setOrange(String(p.orange)); setRed(String(p.red));
    setShowErrors(false);
  };
  const isSelected = (p) => nPrep === p.prep && nShoot === p.shoot && nOrange === p.orange && nRed === p.red;

  // ─── Couleur de fond selon la phase ───
  // En phase "idle" (réglages), on suit le thème via var CSS.
  // Les phases actives gardent leurs couleurs vives, indépendantes du thème.
  const cfg = cfgRef.current;
  let bg = "var(--bl-bg, #0a0a0a)", label = "";
  if (phase === "prep") { bg = "#4338ca"; label = t("chrono.prep_phase"); }
  else if (phase === "shoot") {
    if (remaining <= cfg.red) { bg = "#dc2626"; }
    else if (remaining <= cfg.orange) { bg = "#ea580c"; }
    else { bg = "#15803d"; }
    label = t("chrono.shoot_phase");
  } else if (phase === "done") { bg = "#7f1d1d"; label = t("chrono.done"); }

  const idle = phase === "idle";
  const running = phase === "prep" || phase === "shoot";

  const numInput = (val, set, err) => (
    <input type="text" inputMode="numeric" value={val}
      onChange={(e) => { set(e.target.value.replace(/[^0-9]/g, "")); setShowErrors(false); }}
      className={`w-full bg-zinc-950 border outline-none rounded-md px-3 py-2 text-center font-heading font-black text-lg focus:border-cyan-400 ${showErrors && err ? "border-red-500" : "border-zinc-800"}`} />
  );

  const padRows = [
    ["playPause", t("chronopage.map_play")],
    ["restart", t("chronopage.map_restart")],
  ];

  return (
    <div
      data-testid="chrono-page"
      className={idle ? "text-white" : ""}
      style={{ position: "fixed", inset: 0, zIndex: 10, background: bg, transition: "background 300ms", display: "flex", flexDirection: "column", paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {/* Barre haut */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px", padding: "14px 16px" }}>
        <button
          data-testid="chrono-back"
          onClick={() => navigate("/dashboard")}
          aria-label={t("common.back")}
          style={{ background: "rgba(0,0,0,0.25)", border: "none", color: "#fff", borderRadius: "8px", padding: "8px", cursor: "pointer" }}
        >
          <ArrowLeft size={18} />
        </button>
        <span
          className={idle ? "text-white" : ""}
          style={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: ".2em", fontSize: "13px", ...(idle ? {} : { color: "rgba(255,255,255,0.85)" }) }}
        >
          {t("chrono.title")}
        </span>
        <div style={{ flex: 1 }} />
        {!audioReady && (
          <span style={chipStyle("#fcd34d")}><VolumeX size={14} /> {t("chronopage.audio_hint")}</span>
        )}
        <span data-testid="chrono-wake" style={chipStyle(wake === "on" ? "#86efac" : "#fcd34d")}>
          {wake === "on" ? <MonitorSmartphone size={14} /> : <MonitorOff size={14} />}
          {wake === "on" ? t("chronopage.awake_on") : wake === "pending" ? "…" : t("chronopage.awake_off")}
        </span>
      </div>

      {idle ? (
        // ─── Réglages ───
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 16px 24px" }}>
          <div style={{ maxWidth: "460px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "20px" }}>
            <div>
              <div style={{ color: "#a1a1aa", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em", marginBottom: "8px" }}>{t("chrono.presets")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {BUILTIN.map((p) => (
                  <button key={p.key} onClick={() => applyPreset(p)}
                    className={`px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide bg-zinc-900 border ${isSelected(p) ? "border-cyan-400 text-cyan-400" : "border-zinc-800 text-zinc-200 hover:border-cyan-400/60"}`}>
                    {t(`chrono.${p.key}`)}
                  </button>
                ))}
              </div>
            </div>

            {saved.length > 0 && (
              <div>
                <div style={{ color: "#a1a1aa", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".2em", marginBottom: "8px" }}>{t("chrono.my_presets")}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {saved.map((p, i) => (
                    <div key={i} className={`flex items-center gap-2 bg-zinc-900 border rounded-md px-3 py-2 ${isSelected(p) ? "border-cyan-400" : "border-zinc-800"}`}>
                      <button onClick={() => applyPreset(p)} className={`flex-1 text-left text-sm font-bold ${isSelected(p) ? "text-cyan-400" : "text-zinc-200"}`}>{p.name}</button>
                      <span className="text-xs text-zinc-500">{fmt(p.shoot)}</span>
                      <button onClick={() => persist(saved.filter((_, k) => k !== i))} className="p-1 text-zinc-500 hover:text-red-400"><Trash2 size={15} /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.prep")}</div>{numInput(prep, setPrep, errPrep)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.shoot")}</div>{numInput(shoot, setShoot, errShoot)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">{t("chrono.orange")}</div>{numInput(orange, setOrange, errOrange)}</div>
              <div><div className="text-[11px] uppercase tracking-[0.2em] text-red-400 mb-1">{t("chrono.red")}</div>{numInput(red, setRed, errRed)}</div>
            </div>
            <div className="text-xs text-zinc-500">{t("chrono.times_hint")}</div>
            {showErrors && invalid && <div className="text-xs text-red-400 font-bold">{t("chrono.fix_fields")}</div>}

            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">{t("chrono.name")}</div>
              <input value={name} onChange={(e) => { setName(e.target.value); setNameTried(false); }} placeholder={t("chrono.name_ph")}
                className={`w-full bg-zinc-950 border outline-none rounded-md px-3 py-2 focus:border-cyan-400 ${nameTried && !name.trim() ? "border-red-500" : "border-zinc-800"}`} />
            </div>

            <div className="flex gap-2">
              <button onClick={savePreset} className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:border-zinc-600 text-zinc-200 font-bold uppercase tracking-wide px-4 py-3 rounded-md text-sm">
                <Save size={16} /> {t("chrono.save")}
              </button>
              <button data-testid="chrono-start" onClick={start} disabled={invalid}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-40 font-bold uppercase tracking-wide px-6 py-3 rounded-md">
                <Play size={18} /> {t("chrono.start")}
              </button>
            </div>

            {/* Manette / clavier */}
            <div className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-400 mb-1">
                <Gamepad2 size={14} /> {t("chronopage.controls_title")}
              </div>
              <div className="text-xs text-zinc-500 mb-2">{t("chronopage.controls_desc")}</div>
              <div className={`text-xs mb-3 ${padName ? "text-cyan-400" : "text-zinc-500"}`} data-testid="chrono-pad-status">
                {padName ? `${t("chronopage.pad_connected")} : ${padName}` : t("chronopage.pad_none")}
              </div>
              {padRows.map(([key, rowLabel]) => (
                <div key={key} className="flex items-center gap-2 mb-2">
                  <div className="flex-1 text-sm font-bold text-zinc-200">{rowLabel}</div>
                  <span className="text-xs text-zinc-400 tabular-nums">#{pad[key]}</span>
                  <button
                    onClick={() => setAssigning(assigning === key ? null : key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide border ${assigning === key ? "border-cyan-400 text-cyan-400 animate-pulse" : "border-zinc-700 text-zinc-200 hover:border-zinc-500"}`}
                  >
                    {assigning === key ? t("chronopage.map_press") : t("chronopage.map_assign")}
                  </button>
                </div>
              ))}
              <button onClick={() => { persistPad(DEFAULT_PAD); setAssigning(null); }} className="text-xs text-zinc-500 hover:text-zinc-300 underline">
                {t("chronopage.map_default")}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // ─── Chrono en cours / terminé ───
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "24px", padding: "16px" }}>
          <div style={{ color: "rgba(255,255,255,0.9)", textTransform: "uppercase", letterSpacing: ".3em", fontWeight: 800, fontSize: "clamp(14px,4vw,22px)" }}>{label}</div>
          <div style={{ color: "#fff", fontWeight: 900, fontFamily: "monospace", lineHeight: 1, fontSize: "clamp(80px,26vw,240px)" }}>
            {phase === "done" ? "0s" : fmt(remaining)}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px" }}>
            {running && !paused && (
              <button data-testid="chrono-pause" onClick={pause} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><Pause size={22} /> {t("chrono.pause")}</button>
            )}
            {running && paused && (
              <button data-testid="chrono-resume" onClick={resume} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><Play size={22} /> {t("chrono.resume")}</button>
            )}
            <button data-testid="chrono-restart" onClick={restart} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><RotateCcw size={22} /> {t("chrono.restart")}</button>
            <button data-testid="chrono-settings" onClick={reset} className="inline-flex items-center gap-2 bg-black/30 hover:bg-black/40 text-[#fff] font-bold uppercase tracking-wide px-8 py-4 rounded-md text-lg"><SettingsIcon size={22} /> {t("chrono.settings")}</button>
          </div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", textTransform: "uppercase", letterSpacing: ".15em", textAlign: "center" }}>
            {t("chronopage.controls_hint")}
          </div>
        </div>
      )}
    </div>
  );
}
